import { normalizeChartScale } from "./lightweightChartUtils";
import { INTRADAY_SESSION_SCALES } from "./chartSessionOverlay";
import {
  colorWithAlpha,
  overlayAppliesToTicker,
} from "./chartOverlayUtils";

const INDICATOR_COLORS = [
  "#f59e0b",
  "#06b6d4",
  "#a855f7",
  "#ef4444",
  "#22c55e",
  "#64748b",
];

const closeValue = (candle) => {
  const value = candle?.close ?? candle?.value;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const movingAverageData = (candleData, length) => {
  const windowLength = Math.max(1, Number(length) || 20);
  const result = [];
  let sum = 0;
  const values = [];

  candleData.forEach((candle) => {
    const close = closeValue(candle);
    values.push(close);
    if (close != null) sum += close;
    if (values.length > windowLength) {
      const removed = values.shift();
      if (removed != null) sum -= removed;
    }
    if (values.length === windowLength && values.every((value) => value != null)) {
      result.push({ time: candle.time, value: sum / windowLength });
    }
  });

  return result;
};

const exponentialAverageData = (candleData, length) => {
  const windowLength = Math.max(1, Number(length) || 20);
  const multiplier = 2 / (windowLength + 1);
  const result = [];
  let ema = null;

  candleData.forEach((candle) => {
    const close = closeValue(candle);
    if (close == null) return;
    ema = ema == null ? close : close * multiplier + ema * (1 - multiplier);
    result.push({ time: candle.time, value: ema });
  });

  return result;
};

const vwapData = (candleData) => {
  let cumulativePriceVolume = 0;
  let cumulativeVolume = 0;
  const result = [];

  candleData.forEach((candle) => {
    const high = Number.isFinite(candle?.high) ? candle.high : closeValue(candle);
    const low = Number.isFinite(candle?.low) ? candle.low : closeValue(candle);
    const close = closeValue(candle);
    const volume = Number(candle?.volume);
    if (high == null || low == null || close == null || !Number.isFinite(volume) || volume <= 0) return;

    cumulativePriceVolume += ((high + low + close) / 3) * volume;
    cumulativeVolume += volume;
    if (cumulativeVolume > 0) {
      result.push({ time: candle.time, value: cumulativePriceVolume / cumulativeVolume });
    }
  });

  return result;
};

const bollingerData = (candleData, length, multiplier) => {
  const windowLength = Math.max(1, Number(length) || 20);
  const mult = Number.isFinite(Number(multiplier)) ? Number(multiplier) : 2;
  const upper = [];
  const middle = [];
  const lower = [];
  const values = [];

  candleData.forEach((candle) => {
    const close = closeValue(candle);
    values.push(close);
    if (values.length > windowLength) values.shift();
    if (values.length !== windowLength || values.some((value) => value == null)) return;

    const mean = values.reduce((sum, value) => sum + value, 0) / windowLength;
    const variance =
      values.reduce((sum, value) => sum + (value - mean) ** 2, 0) / windowLength;
    const deviation = Math.sqrt(variance);
    middle.push({ time: candle.time, value: mean });
    upper.push({ time: candle.time, value: mean + deviation * mult });
    lower.push({ time: candle.time, value: mean - deviation * mult });
  });

  return { upper, middle, lower };
};

export const buildIndicatorSeriesConfigs = ({ agenticChartState, selectedTickerId, candleData, scale }) => {
  if (!agenticChartState || typeof agenticChartState !== "object") return [];
  if (!Array.isArray(candleData) || candleData.length === 0) return [];

  const normalizedScale = normalizeChartScale(scale);
  const indicators = Array.isArray(agenticChartState.indicators)
    ? agenticChartState.indicators
    : [];
  const configs = [];

  indicators.forEach((indicator, index) => {
    if (!indicator || typeof indicator !== "object") return;
    if (!overlayAppliesToTicker(indicator, selectedTickerId)) return;
    if (String(indicator.pane || "main").toLowerCase() !== "main") return;

    const kind = String(indicator.kind || "").toLowerCase();
    const params = indicator.params || {};
    const color = INDICATOR_COLORS[index % INDICATOR_COLORS.length];
    const label = indicator.label || kind.toUpperCase();

    if (kind === "sma") {
      configs.push({
        id: indicator.id || `${kind}-${params.length || 20}`,
        label,
        color,
        data: movingAverageData(candleData, params.length || 20),
      });
    } else if (kind === "ema") {
      configs.push({
        id: indicator.id || `${kind}-${params.length || 20}`,
        label,
        color,
        data: exponentialAverageData(candleData, params.length || 20),
      });
    } else if (kind === "vwap" && INTRADAY_SESSION_SCALES.has(normalizedScale)) {
      configs.push({
        id: indicator.id || "vwap",
        label,
        color,
        data: vwapData(candleData),
      });
    } else if (kind === "bollinger_bands") {
      const bands = bollingerData(
        candleData,
        params.length || 20,
        params.multiplier || 2,
      );
      configs.push({
        id: `${indicator.id || "bollinger"}-upper`,
        label: `${label} upper`,
        color,
        data: bands.upper,
      });
      configs.push({
        id: `${indicator.id || "bollinger"}-middle`,
        label: `${label} mid`,
        color: colorWithAlpha(color, 0.72),
        data: bands.middle,
        lineWidth: 1,
      });
      configs.push({
        id: `${indicator.id || "bollinger"}-lower`,
        label: `${label} lower`,
        color,
        data: bands.lower,
      });
    }
  });

  return configs.filter((config) => config.data.length > 0);
};
